import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Chase, ChaseRung } from "@/campaigns/modules/chase/types";
import {
  chasePhase,
  inReachRung,
  spinsUsed,
} from "@/campaigns/modules/chase/types";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { PrizeText } from "@/components/prize-text";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";

/** What a rung is to this player right now. */
type RungState = "won" | "inReach" | "open" | "out";

const rungState = (
  rung: ChaseRung,
  inReach: ChaseRung | null,
  used: number | null
): RungState => {
  if (rung.won) return "won";
  if (inReach?.id === rung.id) return "inReach";
  if (used !== null && rung.budget < used) return "out";
  return "open";
};

/**
 * The prize ladder: one plain row per rung — the stretch of spins it covers on the left
 * ("11–20 spins"), the prize on the right, its games under it — cheapest budget first. The rung finishing now would pay wears a
 * gold rim and gold type; a won rung reads as done — the prize in the muted "finished" tone behind
 * the mission rows' "Done"-style badge (here "Won"); a rung this run has spent past dims as a whole.
 * Says nothing the "Current step" card does not; it is the map that card points into.
 */
export const ChaseLadder = ({
  chase,
  className,
}: {
  chase: Chase;
  className?: string;
}) => {
  const { t } = useTranslation();
  const used = spinsUsed(chase);
  // Nothing is in reach once the run is over: the won rungs are the result, the rest is history.
  const inReach = chasePhase(chase) === "chasing" ? inReachRung(chase) : null;

  if (chase.rungs.length === 0) return null;

  return (
    <Card title={t("tournament.prizesHeading", "Prizes")} className={className}>
      <ul className="flex w-full flex-col gap-2">
        {chase.rungs.map((rung, index) => {
          const state = rungState(rung, inReach, used);
          const lit = state === "inReach";
          // The stretch of spins this rung covers: from just past the previous budget to its own.
          const from = index === 0 ? 1 : chase.rungs[index - 1].budget + 1;
          return (
            <li
              key={rung.id}
              data-state={state}
              className={cn(
                "flex items-center justify-between gap-3 rounded-(--border-radius-inner) border px-3 py-2.5",
                lit
                  ? "border-prizes bg-prizes/[0.08]"
                  : "border-foreground/[0.06] bg-background-card-surface",
                state === "out" && "opacity-55"
              )}
            >
              <span
                className={cn(
                  "shrink-0 font-heading text-[12px] leading-4 font-semibold tabular-nums lg:text-[13px]",
                  lit ? "text-prizes" : "text-foreground-secondary"
                )}
              >
                {t("chase.spinsInterval", "{{from}}–{{to}} spins", {
                  from,
                  to: rung.budget,
                })}
              </span>
              <span className="flex min-w-0 flex-col items-end gap-1">
                <span className="flex max-w-full min-w-0 items-center gap-1.5">
                  {state === "won" && (
                    <Badge
                      variant="tonal"
                      color="prizes"
                      icon={<Check strokeWidth={3} />}
                      className="shrink-0"
                    >
                      {t("chase.rungWon", "Won")}
                    </Badge>
                  )}
                  {/* Never truncated: the prize shrinks, then glides on its one row. */}
                  <PrizeText
                    size="inherit"
                    weight={lit ? "bold" : "medium"}
                    // Won: the badge carries the gold; the prize is the frozen figure (`muted`).
                    tone={lit ? "gold" : state === "won" ? "muted" : "flat"}
                    shadow={false}
                    fit="marquee"
                    className={cn(
                      "max-w-full min-w-0 text-right font-heading text-[13px] leading-[18px] lg:text-[14px] lg:leading-5",
                      !lit && state !== "won" && "text-prizes/75"
                    )}
                  >
                    {formatPrizeTitle(rung.prize.title, t)}
                  </PrizeText>
                </span>
                <PrizeGamesChips
                  games={rung.prize.games ?? []}
                  className="justify-end"
                />
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};
